import { useState, useCallback, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push as navigateTo } from 'connected-react-router';
import { State } from 'reducers';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { getVideoCall, getVideoCalls } from 'selectors/ecsSelectors';
import { Triangle } from 'ui/shared/shapes/Triangle';
import { Link } from 'react-router-dom';
import { VideoCallEventRecordSet } from '../videoCallEventRecordSet/VideoCallEventRecordSet';
import { sendEcsMSCallMessage } from 'actions/ecsActions';
import { useWebSocket } from 'hooks/useWebSocket/useWebSocket';
import { animated, useSpring } from 'react-spring';
import { DownloadButton } from '../downloadButton/DownloadButton';
import { makeCallArchiveFilename } from './makeCallArchiveFilename';
import { values } from 'lodash';
import { useUser } from 'hooks/useUser/useUser';
import { getWebSocketConnected } from 'selectors/websocketSelectors';
import { useBuilding } from 'hooks/useBuilding/useBuilding';
import { useVideoCallExport } from './useVideoCallExport';

require('./VideoCallRecord.css');

const UNANSWERED_CALL_EXPIRY = 15 * 60 * 1000; // 15 minutes
const CALLBACK_WINDOW_EXPIRY = 15 * 60 * 1000; // 15 minutes

export type Props = {
    callId: string;
    expanded?: boolean;
};

export const VideoCallRecord = (props: Props) => {
    const { callId } = props;
    
    const { t, i18n } = useTranslation();
    const websocketClient = useWebSocket();

    const [ expanded, setExpanded ] = useState(props.expanded || false);
    
    const allVideoCalls = useSelector(getVideoCalls);
    const websocketConnected = useSelector(getWebSocketConnected);

    const videoCall = useSelector((state: State) => getVideoCall(state, props));
    const {
        source = 'EL',
        elevatorId = null,
        externalId = null,
        buildingId = null,
        jpeg = null,
        created = 0,
        joined = null,
        ended = null,
        userId = null,
        elevatorName = null,
    } = videoCall || {};
    
    const {
        beginExport,
        clearExport,
        downloading,
        exportUrl,
        exportPercent,
        exportErrorMessage,
    } = useVideoCallExport(callId);

    const { building, isLoadingBuilding } = useBuilding(buildingId);
    const { streetNumber, streetName, city, region, country } = building || {} as any;
    const buildingName = building && building.displayName;

    const elevatorDisplayName = elevatorName || externalId || t('NO_NAME');
    const elevatorDownloadId =  elevatorName || externalId || elevatorId;

    const { user, isLoadingUser } = useUser(userId);
    const userName = user && (user.displayName || user.id);


    const now = Date.now();

    const callbackStyle = useSpring({
        from: {
            position: 'relative',
            opacity: 0,
            height: 0,
        },
        to: {
            opacity: 1,
            height: 52,
        },
        reverse: !expanded || !ended || !(now - ended < CALLBACK_WINDOW_EXPIRY),
    });

    const dispatch = useDispatch();

    const handleClickCallback = useCallback(() => {
        if (elevatorId) {
            // But first check if there is a pending unanswered call for this elevator.
            // Should be one or zero
            const pendingElevatorCalls =
                values(allVideoCalls)
                    .filter(c =>
                        c.source === 'EL' &&
                        c.elevatorId === elevatorId &&
                        !c.joined &&
                        !c.ended
                    );

            // Answer the call
            if (pendingElevatorCalls.length) {
                const { callId } = pendingElevatorCalls[0];
                console.log(`Redirecting to call ${callId}`);
                dispatch(navigateTo(`/video/${callId}`));
                return;
            }

            // Otherwise, request a new call
            dispatch(sendEcsMSCallMessage(elevatorId, { sent: Date.now() }));
            websocketClient.ecsCall(elevatorId, false);
        }
    }, [ elevatorId, dispatch, websocketClient, allVideoCalls ]);

    const isFinished = !!ended;
    const isExpired = !joined && now > created + UNANSWERED_CALL_EXPIRY;
    const isPending = !joined && !isExpired && !isFinished;
    const isActive = !!joined && !ended;

    const waitTimeLabel = moment.duration((joined || now) - created, 'ms').locale(i18n.language).humanize();

    const durationString =
        ended
            ? moment.duration(ended - (joined || created), 'ms').locale(i18n.language).humanize()
            : joined
                ? moment.duration(now - joined, 'ms').locale(i18n.language).humanize()
                : '';
    const createdToEndedLabel = `${moment.utc(joined || created).local().format('LT')} - ${ended ? moment.utc(ended).local().format('LT') : '?'}`;

    const timeRemaining = moment.duration(ended! + UNANSWERED_CALL_EXPIRY - now);
    const timeRemainingLabel = t('VIDEO.TIME_REMAINING', { time: `${timeRemaining.minutes()}:${timeRemaining.seconds().toString().padStart(2, '0')}` });

    const recordClassName =
        isPending
            ? 'pending'
            : isActive
                ? 'active'
                : 'recent';

    const callbackButtonMounted = ended && (now - ended < CALLBACK_WINDOW_EXPIRY + 500); // a little extra time for animation

    return (
        <div
            id={ `call-${callId}` }
            className="video-call-record"
        >
            <header>
                <div className={ `status ${recordClassName}` }/>

                <div
                    className="expand-collapse"
                    onClick={ () => setExpanded(!expanded) }
                >
                    <Triangle className={ expanded ? 'expanded' : 'collapsed' }/>
                </div>

                <div
                    className="created"
                    title={ moment.utc(created).local().format('LTS') }
                >
                    { moment.utc(created).local().format('LT') }
                </div>

                <div className="source">
                    { source === 'EL'
                        ? <i className="ui large blue phone icon" title={ t('VIDEO.CALL_FROM_EL') }/>
                        : source === 'LP'
                            ? <i className="ui large grey building outline icon" title={ t('VIDEO.CALL_FROM_LP') }/>
                            : <i className="ui large grey desktop icon" title={ t('VIDEO.CALL_FROM_MS') }/>
                    }
                </div>

                <div className="call-id-host">
                    <span className="symbol">#</span>
                    <span className="call-id">
                        { callId }
                    </span>
                    { isActive &&
                        <span className="active-indicator"/>
                    }

                    { ended && !exportUrl && !exportErrorMessage &&
                        <DownloadButton
                            downloading={ downloading }
                            percent={ exportPercent }
                            onClick={ beginExport }
                        />
                    }
                    { exportUrl && !exportErrorMessage &&
                        <a
                            className="download-link"
                            href={ exportUrl }
                            download={ makeCallArchiveFilename(callId, externalId || elevatorId, elevatorDownloadId, created) }
                            rel="noopener"
                        >
                            { t('VIDEO.SAVE_AS') }
                        </a>
                    }
                    { exportErrorMessage &&
                        <i
                            className="ui red warning circle icon"
                            onClick={ clearExport }
                            title={ exportErrorMessage }
                        />
                    }
                </div>

                <div className="thumbnail-host">
                    { jpeg &&
                        <img
                            className="thumbnail"
                            src={ `data:image/jpeg;base64,${jpeg}` }
                            draggable={ false }
                            alt=""
                        />
                    }
                </div>

                <div className="where">
                    <div className="elevator-name">
                        { elevatorDisplayName }
                    </div>
                    <div className="building-name">
                        { isLoadingBuilding ? t('LOADING') : building && !building.enabled ? t('VIDEO.NEW_BUILDING') : buildingName }
                    </div>
                    <div className="building-address">
                        { building && region && country ? `${streetNumber || ''} ${streetName || ''} ${city || ''} ${region} ${country}` : t('VIDEO.UNKNOWN_ADDRESS') }
                    </div>
                </div>

                <div className="responder">
                    { source === 'EL' && isPending &&
                        <Link
                            className={ `connect-button ${websocketConnected ? '' : 'disabled'}` }
                            to={ `/video/${callId}` }
                        >
                            <div className={ `ui ${websocketConnected ? '' : 'disabled'} primary button` }>
                                { t('VIDEO.CONNECT') }
                            </div>
                        </Link>
                    }
                    { joined && userId &&
                        <Fragment>
                            <div className="symbol">
                                { t('VIDEO.ANSWERED_BY') }
                            </div>
                            <div className="user-name">
                                { isLoadingUser ? t('LOADING') : userName }
                            </div>
                        </Fragment>
                    }
                    { joined && !userId &&
                        <Fragment>
                            <div className="symbol">
                                { t('VIDEO.ANSWERED_BY') }
                            </div>
                            <div>
                                { t('VIDEO.LOBBY_PANEL') }
                            </div>
                        </Fragment>
                    }
                    { isExpired &&
                        <div className="expired">
                            { t('VIDEO.EXPIRED') }
                        </div>
                    }
                </div>
            
                <div className="stats">
                    { isPending &&
                        <Fragment>
                            <div className="symbol">
                                { t('VIDEO.WAIT_TIME') }
                            </div>
                            <div title={ createdToEndedLabel }>
                                { waitTimeLabel }
                            </div>
                        </Fragment>
                    }
                    { isActive &&
                        <Fragment>
                            <div className="symbol">
                                { t('VIDEO.DURATION') }
                            </div>
                            <div title={ createdToEndedLabel }>
                                { durationString }
                            </div>
                        </Fragment>
                    }
                    { joined && ended && // Completed
                        <Fragment>
                            <div className="symbol">
                                { t('VIDEO.DURATION') }
                            </div>
                            <div title={ createdToEndedLabel }>
                                { durationString }
                            </div>
                        </Fragment>
                    }
                </div>
            </header>
            <footer>
                <VideoCallEventRecordSet
                    callId={ callId }
                    visible={ expanded }
                />
                { callbackButtonMounted &&
                    <animated.div
                        style={ callbackStyle as any }
                    >
                        <div className="call-back-host">
                            <div
                                className={ `ui ${websocketConnected ? '' : 'disabled'} primary button` }
                                onClick={ handleClickCallback }
                                tabIndex={ 0 }
                            >
                                { t('VIDEO.CALL_BACK') }
                            </div>
                            <div className="call-back-window">
                                { timeRemainingLabel }
                            </div>
                        </div>
                    </animated.div>
                }
            </footer>
        </div>
    );
};
